import React, { useState, useMemo, useEffect } from "react";
import HomeStays from "@tripbuilder/cat-accommodation/right/HomeStays";
import Hotels from "@tripbuilder/cat-accommodation/right/Hotels";
import { AnimatePresence } from "framer-motion";
import logger from "@/utils/logger";
import {
  AccommodationListing,
  HotelInitial,
  RoomDetails,
  ViewLodging,
} from "@/types";
import { convertHotelIntoTripLodging } from "@/lib/accommodation/agodaHotels";
import useAccommodationStore from "@/stores/tripBuilder/useAccommodation";
import useTripStore from "@/stores/tripBuilder/useTrip";
import useTransportStore from "@/stores/tripBuilder/useTransport";
import { DateTime } from "luxon";
import MainLodgingModal from "@tripbuilder/cat-accommodation/right/MainLodgingModal";
import { convertHomeStayIntoTripLodging } from "@/lib/accommodation/homes";
import { checkHomeStayAvailability } from "@/lib/accommodation/plumGuide";
import { useToast } from "@/hooks/use-toast";
import CustomAccommodationForm from "@tripbuilder/cat-accommodation/right/custom/CustomAccommodationForm";
import AccommodationTopBar from "@tripbuilder/cat-accommodation/right/topBar";
import { useRateLimitStore } from "@/stores/rateLimitStore";
import useUserStore from "@/stores/user/authStore";

const AccommodationRhs: React.FC = () => {
  const [selectedRoom, setSelectedRoom] = useState<RoomDetails | null>(null);

  // User
  const anonymousUser = useUserStore((state) => state.anonymousUser);

  // Trip Store
  const tripData = useTripStore((state) => state.tripData);
  const selectedLocation = useTripStore((state) => state.selectedLocation);
  const checkIsAdmin = useTripStore((state) => state.checkIsAdmin);

  // Accommodation Store
  const addLodging = useAccommodationStore((state) => state.addLodging);
  const activeAccommodationTab = useAccommodationStore(
    (state) => state.activeAccommodationTab
  );
  const viewingLodging = useAccommodationStore((state) => state.viewingLodging);
  const setLastAddedHotel = useAccommodationStore(
    (state) => state.setLastAddedHotel
  );
  const setShowAddNotification = useAccommodationStore(
    (state) => state.setShowAddNotification
  );
  const isMainLodgingModalOpen = useAccommodationStore(
    (state) => state.isMainLodgingModalOpen
  );
  const setIsMainLodgingModalOpen = useAccommodationStore(
    (state) => state.setIsMainLodgingModalOpen
  );
  const setMainLodging = useAccommodationStore((state) => state.setMainLodging);
  const coreLodging = useAccommodationStore((state) => state.coreLodging);
  const setAddingLodging = useAccommodationStore(
    (state) => state.setAddingLodging
  );
  const handleModalOpenHomeStay = useAccommodationStore(
    (state) => state.handleModalOpenHomeStay
  );
  const handleModalOpenMainLodging = useAccommodationStore(
    (state) => state.handleModalOpenMainLodging
  );
  const getAccommodationLength = useAccommodationStore(
    (state) => state.getAccommodationLength
  );

  // Rate Limit Store
  const setRateLimit = useRateLimitStore((state) => state.setRateLimit);

  // Transport Store
  const getEffectiveArrivalDate = useTransportStore(
    (state) => state.getEffectiveArrivalDate
  );

  // Local State
  const [isMoreInfoModalOpen, setIsMoreInfoModalOpen] = useState(false);

  // Toast
  const { toast } = useToast();

  // Admin Check
  const isAdmin = useMemo(() => checkIsAdmin(), [checkIsAdmin]);

  // Active Tab
  const activeTab =
    activeAccommodationTab === "hotels"
      ? 0
      : activeAccommodationTab === "home-stays"
      ? 1
      : 2;

  // Effective Check In
  const effectiveCheckIn = useMemo(() => {
    const date = getEffectiveArrivalDate(
      tripData!.transport,
      selectedLocation!.id
    );
    return date ? date : selectedLocation!.date.start_date;
  }, [tripData, selectedLocation, getEffectiveArrivalDate]);

  // Effective Check Out
  const effectiveCheckOut = useMemo(() => {
    return DateTime.fromISO(selectedLocation!.date.end_date)
      .setZone(selectedLocation!.date.time_zone)
      .toFormat("yyyy-MM-dd");
  }, [selectedLocation]);

  // Handle Add Hotel
  const handleAddHotel = async (selectedHotel: HotelInitial) => {
    if (!tripData) return;
    if (getAccommodationLength(tripData!) >= 1 && anonymousUser) {
      setRateLimit(
        "Create your account to add more lodgings to your trip.",
        tripData?.name
      );
      return;
    }
    const tripLodging = await convertHotelIntoTripLodging(
      selectedHotel,
      effectiveCheckIn,
      effectiveCheckOut,
      selectedLocation!.date.time_zone,
      selectedLocation!.date.num_days - 1,
      selectedRoom ?? undefined
    );

    addLodging(tripLodging);
    setLastAddedHotel(tripLodging);
    setShowAddNotification(true);

    setTimeout(() => {
      setShowAddNotification(false);
      setLastAddedHotel(null);
    }, 5000);
  };

  // Handle Add Home Stay
  const handleAddHomeStay = async (
    selectedAccommodation: AccommodationListing
  ) => {
    if (!tripData) return;
    if (getAccommodationLength(tripData!) >= 1 && anonymousUser) {
      setRateLimit(
        "Create your account to add more lodgings to your trip!",
        tripData?.name
      );
      return;
    }
    setAddingLodging(true, selectedAccommodation.listingid);

    try {
      const availabilityCheck = await checkHomeStayAvailability(
        selectedAccommodation.listingid,
        effectiveCheckIn,
        effectiveCheckOut
      );

      logger.log("availabilityCheck", availabilityCheck);

      if (!availabilityCheck.isAvailable) {
        toast({
          title: "Sorry, this home stay is not available",
          description: availabilityCheck.reason,
          variant: "warning",
        });
        setAddingLodging(false, null);
        return;
      }

      const tripLodging = convertHomeStayIntoTripLodging(
        selectedAccommodation,
        effectiveCheckIn,
        effectiveCheckOut,
        selectedLocation!.date.time_zone,
        selectedLocation!.date.num_days - 1
      );

      addLodging(tripLodging);
      setLastAddedHotel(tripLodging);
      setShowAddNotification(true);

      // if selectedLocation.accommodation.main is null, set it to the new lodging
      if (!selectedLocation?.accommodation.main) {
        setMainLodging(tripLodging);
      }
      setAddingLodging(false, null);

      setTimeout(() => {
        setShowAddNotification(false);
        setLastAddedHotel(null);
      }, 5000);
    } catch (error) {
      toast({
        title: "Error checking availability",
        description: "Please try again later",
        variant: "destructive",
      });
      setAddingLodging(false, null);
    }
  };

  const handleAddLodgingModal = async (
    viewLodging: ViewLodging,
    lodging: HotelInitial | AccommodationListing
  ) => {
    if (viewLodging.type === "hotel") {
      handleAddHotel(lodging as HotelInitial);
    } else {
      logger.log("Adding home stay:", lodging);
      handleAddHomeStay(lodging as AccommodationListing);
    }
  };

  useEffect(() => {
    if (isMainLodgingModalOpen) {
      setIsMoreInfoModalOpen(false);
    }

    return () => {
      if (isMainLodgingModalOpen) {
        setIsMainLodgingModalOpen(false);
      }
    };
  }, [isMainLodgingModalOpen, setIsMainLodgingModalOpen]);

  // Clean up selectedRoom when component unmounts
  useEffect(() => {
    return () => {
      setSelectedRoom(null);
      setIsMainLodgingModalOpen(false);
    };
  }, []);

  return (
    <div className="accommodation-content w-full h-full mt-2 flex flex-col items-center">
      <div
        className={`w-full ${
          isMoreInfoModalOpen || isMainLodgingModalOpen
            ? "opacity-50 backdrop-blur-2xl"
            : ""
        }`}
      >
        <div className="py-2 rounded-xl w-full">
          <AccommodationTopBar
            effectiveCheckIn={effectiveCheckIn}
            isAdmin={isAdmin}
          />
        </div>
        <div className="flex flex-col min-h-full items-center justify-start space-y-4 pt-2 rounded-lg overflow-y-scroll no-scrollbar">
          {selectedLocation?.coordinates &&
            (activeTab === 0 ? (
              <Hotels
                checkIn={effectiveCheckIn}
                checkOut={selectedLocation.date.end_date}
                handleAddToTrip={handleAddHotel}
                handleModalOpenMainLodging={handleModalOpenMainLodging}
                isAdmin={isAdmin}
              />
            ) : activeTab === 1 ? (
              <HomeStays
                handleAddToTrip={handleAddHomeStay}
                handleModalOpenMainLodging={handleModalOpenHomeStay}
                isAdmin={isAdmin}
              />
            ) : (
              <CustomAccommodationForm />
            ))}
        </div>
      </div>
      <div className="flex items-center justify-center">
        <AnimatePresence mode="wait">
          {isMainLodgingModalOpen && selectedLocation && (
            <MainLodgingModal
              location={selectedLocation}
              lodging={viewingLodging!}
              coreLodging={coreLodging as HotelInitial | AccommodationListing}
              setIsOpen={setIsMainLodgingModalOpen}
              checkIn={effectiveCheckIn}
              checkOut={selectedLocation.date.end_date}
              handleAddLodgingModal={handleAddLodgingModal}
              selectedRoom={selectedRoom}
              setSelectedRoom={setSelectedRoom}
              isAdmin={isAdmin}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AccommodationRhs;
